import { faCircle, faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { QuestionProgressDtoStatusEnum } from '../../../openapi/atlantis';
import { ContentItem } from '../../../openapi/sherlock';
import { SectionProgressDto } from '../../../openapi/yenta';
import { useAppSelector } from '../../../slices/store';
import { removeHtmlTagsFromString } from '../../../utils/StringUtils';
import { cn } from '../../../utils/classUtils';

interface VerticalStepQuestionsV2Props {
  step: SectionProgressDto;
  onNavigateToQuestion: (questionId: string) => void;
}

const VerticalStepQuestionsV2: React.FC<VerticalStepQuestionsV2Props> = ({
  step,
  onNavigateToQuestion,
}) => {
  const { applicationId } = useParams();
  const [selectingQuestion, setSelectingQuestion] = useState<string>();
  const currentQuestion = useAppSelector(
    (state) => state.application.currentQuestion,
  );

  // Add refs for scrolling
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const currentQuestionRef = useRef<HTMLButtonElement>(null);

  const isAgreementsStep = useMemo(() => {
    return step?.name === 'Agreements';
  }, [step?.name]);

  // Auto-scroll to center the current question
  useEffect(() => {
    // Add a small delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      if (currentQuestionRef.current) {
        currentQuestionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center', // This centers the element vertically
          inline: 'nearest', // Don't affect horizontal scrolling
        });
      }
    }, 100); // 100ms delay to ensure DOM is ready

    return () => clearTimeout(timer);
  }, [currentQuestion]); // Re-run when currentQuestion changes

  return (
    <div
      ref={scrollContainerRef}
      className='bg-white pt-4 mt-4 h-72 overflow-y-scroll [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full'
    >
      <div>
        {step.questionProgresses?.map((question, index) => {
          const isCompletedQuestion =
            question.status === QuestionProgressDtoStatusEnum.Completed;

          const isCurrentQuestion =
            (currentQuestion as ContentItem)?.id === question.id;

          const isSelectingQuestion = selectingQuestion === question.id;

          const isInactiveQuestion =
            isSelectingQuestion || (!isCompletedQuestion && !isCurrentQuestion);

          const isLastQuestion =
            index === step?.questionProgresses?.length! - 1;

          return (
            <button
              key={question.id}
              ref={isCurrentQuestion ? currentQuestionRef : null}
              className={cn(
                'flex flex-row items-center space-x-2.5 text-start w-full',
                isAgreementsStep ||
                  (applicationId && step.status === 'COMPLETED')
                  ? 'cursor-auto'
                  : 'cursor-pointer',
                isLastQuestion ? 'mb-0' : 'mb-4',
              )}
              onClick={async () => {
                if (
                  isAgreementsStep ||
                  (applicationId && step.status === 'COMPLETED')
                ) {
                  return;
                }
                if (isCompletedQuestion) {
                  setSelectingQuestion(question.id);
                  await onNavigateToQuestion(question.id!);
                  setSelectingQuestion(undefined);
                }
              }}
            >
              <FontAwesomeIcon
                icon={isCompletedQuestion ? faCircleCheck : faCircle}
                className={cn(
                  isInactiveQuestion
                    ? 'text-reskin-regent-400'
                    : 'text-reskin-primary-blue',
                )}
                fontSize={20}
              />
              <p
                className={cn(
                  'font-inter text-xs',
                  isInactiveQuestion
                    ? 'text-reskin-regent-400'
                    : isCurrentQuestion
                    ? 'text-reskin-primary-dark font-semibold'
                    : 'text-reskin-primary-dark',
                )}
              >
                {removeHtmlTagsFromString(question.title!)}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default VerticalStepQuestionsV2;
